import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * AuthCallback Component
 * Handles the OAuth callback after Google authentication.
 * Extracts session_id from URL fragment and exchanges it for session_token.
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const params = new URLSearchParams(hash.substring(1));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Authentication failed - no session ID');
          navigate('/login');
          return;
        }

        // Exchange session_id for session_token
        const response = await fetch(`${BACKEND_URL}/api/auth/session`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Authentication failed');
        }

        const userData = await response.json();

        // Store user data in localStorage
        localStorage.setItem('taraanaya_user', JSON.stringify(userData));
        localStorage.setItem('userName', userData.name);
        localStorage.setItem('userEmail', userData.email);
        localStorage.setItem('isAuthenticated', 'true');

        toast.success(`Welcome, ${userData.name}! ✨`);

        // Navigate to dashboard with user data
        navigate('/dashboard', { state: { user: userData } });

      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error(error.message || 'Authentication failed');
        navigate('/login');
      }
    };

    processAuth();
  }, [navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a1929 0%, #0d2438 25%, #1a2f45 50%, #0f1e2e 75%, #0a1929 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column',
      gap: '24px'
    }}>
      {/* Loading Animation */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          border: '3px solid rgba(20, 184, 166, 0.2)',
          borderTopColor: '#14b8a6',
        }}
      />
      
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{
          color: 'rgba(148, 163, 184, 0.9)',
          fontSize: '16px'
        }}
      >
        Completing authentication...
      </motion.p>
    </div>
  );
};

export default AuthCallback;
